// PBX pickers (docs/PBX.md): choose where a call goes instead of typing a number.
//   PbxTargetInput   a kind (extension, ring group, conference room, paging group, voicemail, outside number), then
//                    the number from a list (an outside number is typed). Stored as the plain number, 'VM' = voicemail,
//                    as the engine reads it (pbx/router.lua).
//   NumberListInput  several extension numbers (busy lamp keys), stored as a JSON list
//   MemberTargetInput an extension or an outside number (ring group member: extension_id or external_number)
// Lists come from the admin API, or for the "My phone" page from /me/phone/directory (source = 'me').
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input, Select, Checkbox } from '@/components/ui/input';
import { useRefOptions } from '@/hooks/meta';
import { api } from '@/lib/api';
import { t } from '@/i18n';

type Change = (v: string | null) => void;
type Num = { number: string; name: string };
export const PBX_KINDS = ['EXTENSION', 'RING_GROUP', 'CONFERENCE', 'PAGING'] as const;
const PATHS: Record<string, string> = { EXTENSION: 'extensions', RING_GROUP: 'ring-groups', CONFERENCE: 'conference-rooms', PAGING: 'paging-groups' };
export const TARGET_LABEL: Record<string, string> = {
  EXTENSION: 'Extension', RING_GROUP: 'Ring group', CONFERENCE: 'Conference room', PAGING: 'Paging group',
  VOICEMAIL: 'Voicemail', OUTSIDE: 'Outside number',
};

// the numbers of each kind: { EXTENSION: [{number, name}], ... }
export function usePbxNumbers(source: 'admin' | 'me' = 'admin', kinds: readonly string[] = PBX_KINDS): { lists: Record<string, Num[]>; loaded: boolean } {
  const admin = source === 'admin';
  const ext = useRefOptions(PATHS.EXTENSION, admin && kinds.includes('EXTENSION'));
  const rg = useRefOptions(PATHS.RING_GROUP, admin && kinds.includes('RING_GROUP'));
  const conf = useRefOptions(PATHS.CONFERENCE, admin && kinds.includes('CONFERENCE'));
  const pg = useRefOptions(PATHS.PAGING, admin && kinds.includes('PAGING'));
  const me = useQuery({ queryKey: ['me-phone-directory'], enabled: !admin, staleTime: 30_000,
    queryFn: () => api<Record<string, Num[]>>('/me/phone/directory') });
  if (!admin) return { lists: { EXTENSION: [], RING_GROUP: [], CONFERENCE: [], PAGING: [], ...(me.data ?? {}) }, loaded: !me.isLoading };
  const rows = (q: typeof ext) => (q.data ?? []).map((r) => ({ number: String(r.number), name: String(r.name ?? '') }));
  const qs = { EXTENSION: ext, RING_GROUP: rg, CONFERENCE: conf, PAGING: pg } as Record<string, typeof ext>;
  return {
    lists: { EXTENSION: rows(ext), RING_GROUP: rows(rg), CONFERENCE: rows(conf), PAGING: rows(pg) },
    loaded: kinds.every((k) => !qs[k] || !qs[k].isLoading),
  };
}

// which kind a stored value is (null while the lists load)
export function targetKind(value: string | null | undefined, lists: Record<string, Num[]>, kinds: readonly string[]): string {
  const v = String(value ?? '');
  if (!v) return '';
  if (v.toUpperCase() === 'VM' && kinds.includes('VOICEMAIL')) return 'VOICEMAIL';
  for (const k of PBX_KINDS) if (kinds.includes(k) && (lists[k] ?? []).some((r) => r.number === v)) return k;
  return 'OUTSIDE';
}

// "2003 · Ann desk", "Voicemail", "0044 20 ..." for lists
export function targetText(value: string | null | undefined, lists: Record<string, Num[]>): string {
  const v = String(value ?? '');
  if (!v) return '';
  if (v.toUpperCase() === 'VM') return t('Voicemail');
  for (const k of PBX_KINDS) {
    const r = (lists[k] ?? []).find((x) => x.number === v);
    if (r) return `${t(TARGET_LABEL[k])} ${r.number} · ${r.name}`;
  }
  return v;
}

export function PbxTargetInput({ name, value, onChange, targets, source = 'admin' }: {
  name: string; value: string | null | undefined; onChange: Change; targets?: readonly string[]; source?: 'admin' | 'me';
}) {
  const kinds = targets ?? [...PBX_KINDS, 'VOICEMAIL', 'OUTSIDE'];
  const { lists, loaded } = usePbxNumbers(source, kinds);
  const [chosen, setChosen] = React.useState<string | null>(null);
  const kind = chosen ?? (loaded ? targetKind(value, lists, kinds) : '');
  const v = value ?? '';
  function pickKind(k: string) {
    setChosen(k);
    onChange(k === 'VOICEMAIL' ? 'VM' : null);
  }
  const options = PBX_KINDS.includes(kind as typeof PBX_KINDS[number]) ? lists[kind] ?? [] : [];
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-1">
      <Select name={`${name}__kind`} aria-label={t('Send to')} value={kind} onChange={(e) => pickKind(e.target.value)}>
        <option value="">{t('(none)')}</option>
        {kinds.map((k) => <option key={k} value={k}>{t(TARGET_LABEL[k])}</option>)}
      </Select>
      {kind === 'OUTSIDE' && (
        <Input name={name} inputMode="tel" placeholder={t('phone number')} value={v}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9+]/g, '') || null)} />
      )}
      {options && kind !== 'OUTSIDE' && kind !== 'VOICEMAIL' && kind !== '' && (
        <Select name={name} value={v} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">{t('Choose a number…')}</option>
          {v && !options.some((o) => o.number === v) && <option value={v}>{v}</option>}
          {options.map((o) => <option key={o.number} value={o.number}>{`${o.number} · ${o.name}`}</option>)}
        </Select>
      )}
      {(kind === 'VOICEMAIL' || kind === '') && <span />}
    </div>
  );
}

// a JSON list of extension numbers (busy lamp keys), as checkboxes in number order
export function NumberListInput({ name, value, onChange }: { name: string; value: unknown; onChange: (v: string | null) => void }) {
  const { lists } = usePbxNumbers('admin', ['EXTENSION']);
  let picked: string[] = [];
  try { const x = typeof value === 'string' ? JSON.parse(value || '[]') : value; if (Array.isArray(x)) picked = x.map(String); } catch { picked = []; }
  const set = (n: string, on: boolean) => {
    const next = on ? [...picked, n] : picked.filter((p) => p !== n);
    onChange(next.length ? JSON.stringify(next) : null);
  };
  const all = lists.EXTENSION ?? [];
  if (!all.length) return <p className="text-xs text-muted-foreground">{t('No extensions yet.')}</p>;
  return (
    <div className="grid max-h-40 grid-cols-2 gap-1 overflow-y-auto rounded-md border p-2" data-testid={`${name}-list`}>
      {all.map((e) => (
        <label key={e.number} className="flex items-center gap-2 text-sm">
          <Checkbox name={`${name}__${e.number}`} checked={picked.includes(e.number)} onChange={(x) => set(e.number, x.target.checked)} />
          {`${e.number} · ${e.name}`}
        </label>
      ))}
    </div>
  );
}

// ring group member: an extension (extension_id) or an outside number (the paired text field)
export function MemberTargetInput({ name, value, pairName, pairValue, onChange }: {
  name: string; value: unknown; pairName: string; pairValue: unknown; onChange: (v: Record<string, unknown>) => void;
}) {
  const exts = useRefOptions('extensions');
  const [chosen, setChosen] = React.useState<string | null>(null);
  const kind = chosen ?? (pairValue ? 'OUTSIDE' : value ? 'EXTENSION' : '');
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-1">
      <Select name={`${name}__kind`} aria-label={t('Member')} value={kind}
        onChange={(e) => { setChosen(e.target.value); onChange({ [name]: null, [pairName]: null }); }}>
        <option value="">{t('Choose…')}</option>
        <option value="EXTENSION">{t('Extension')}</option>
        <option value="OUTSIDE">{t('Outside number')}</option>
      </Select>
      {kind === 'EXTENSION' && (
        <Select name={name} value={value == null ? '' : String(value)} onChange={(e) => onChange({ [name]: e.target.value ? Number(e.target.value) : null, [pairName]: null })}>
          <option value="">{t('Choose a number…')}</option>
          {(exts.data ?? []).map((r) => <option key={r.id} value={r.id}>{`${r.number} · ${r.name}`}</option>)}
        </Select>
      )}
      {kind === 'OUTSIDE' && (
        <Input name={pairName} inputMode="tel" placeholder={t('phone number')} value={pairValue == null ? '' : String(pairValue)}
          onChange={(e) => onChange({ [pairName]: e.target.value.replace(/[^0-9+]/g, '') || null, [name]: null })} />
      )}
      {kind === '' && <span />}
    </div>
  );
}
